/**
 * Step — Custom or ready-made, by product type (BLU-1373, client variant).
 * Shown by the step model ONLY when the client sells both, right after Tag
 * products. Lists the client's TAGGED Shopify product types (from the
 * streaming coverage scan, tag-gated on the server) and lets an admin pick
 * Custom, Ready-made or Mixed for each:
 *  - Custom writes the type into `customTypes` (the same list Code tables edits);
 *  - Mixed writes it into `mixedTypes`, and each of its products then gets its
 *    own Custom / Ready-made switch in Product sync (default Ready-made);
 *  - Ready-made removes it from both.
 * Entries for types the scan did not list are kept as saved.
 *
 * One Save through the existing settings mutation (`savePatch`). A save that
 * would change how products get SKUs asks first when the org may already have
 * synced products (`SkuChangeConfirmDialog`). Saving needs at least one Custom
 * or Mixed type: a client that sells both has one by definition.
 *
 * The body (settings read + the scan) mounts only while the step is selected
 * and not locked; the scan's pages are cached per tag and shared with Product
 * sync's Code tables tab. Save is the pane footer's primary; its toast names
 * this step. Each type's picker is the shared `SegmentedControl` in the
 * `strong` tone (selected segment deep purple, white text).
 */
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, RefreshCw, Save } from 'lucide-react';
import {
  applyProductTypeKind,
  customDecisionChanged,
  productTypeKind,
  type ProductSyncSettings,
  type ProductTypeKind,
} from '@threadheads/shared';
import { Button } from '@/components/ui/button';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { useRole } from '@/hooks/use-role';
import { useCoverageScan } from '@/features/product-sync/hooks/useCoverageScan';
import { useProductSyncSettingsBuffer } from '@/features/product-sync/hooks/useProductSyncSettingsBuffer';
import { useSavedHold } from '../../hooks/useSavedHold';
import { SkuChangeConfirmDialog } from '../SkuChangeConfirmDialog';
import { StepPane } from '../shell/StepPane';
import { useStepChrome } from '../shell/StepChrome';
import { SavePrimaryButton } from '../shell/SavePrimaryButton';

interface StepTypeKindsProps {
  /** The org's saved sync tag (`''` = none); the scan is keyed by it. */
  syncTag: string;
  /** How many types are saved as Custom and as Mixed (the collapsed summary). */
  typeKinds: { custom: number; mixed: number };
  /** The org may already have synced products: a save that changes SKUs asks first. */
  hasSyncedProducts: boolean;
  /** Refetch the onboarding status after a successful save. */
  onSaved: () => void;
}

/** Display order of the three picks. */
const KIND_ORDER: readonly ProductTypeKind[] = ['custom', 'readyMade', 'mixed'];

const KIND_LABEL_KEY: Record<ProductTypeKind, string> = {
  custom: 'stepTypeKinds.option.custom',
  readyMade: 'stepTypeKinds.option.readyMade',
  mixed: 'stepTypeKinds.option.mixed',
};

function typeKey(name: string): string {
  return name.trim().toLowerCase();
}

/** A type's saved kind, read as the step means it (this step only shows when the client sells both). */
function savedKind(settings: ProductSyncSettings, name: string): ProductTypeKind {
  return productTypeKind({ ...settings, sells: 'both' }, name);
}

interface PaneFrame {
  title: string;
  description: string;
  summary: ReactNode;
}

export function StepTypeKinds({
  syncTag,
  typeKinds,
  hasSyncedProducts,
  onSaved,
}: StepTypeKindsProps) {
  const { t } = useTranslation('onboarding');
  const { state } = useStepChrome();
  const frame: PaneFrame = {
    title: t('stepTypeKinds.title'),
    description: t('stepTypeKinds.description'),
    summary: t('stepTypeKinds.summary', { custom: typeKinds.custom, mixed: typeKinds.mixed }),
  };
  // A locked pane shows only its frame: the settings read and the scan wait.
  if (state === 'locked') return <StepPane {...frame} />;
  return <StepTypeKindsBody frame={frame} syncTag={syncTag} hasSyncedProducts={hasSyncedProducts} onSaved={onSaved} />;
}

/** The step body: its own settings buffer and scan, mounted only while the step is open. */
function StepTypeKindsBody({
  frame,
  syncTag,
  hasSyncedProducts,
  onSaved,
}: { frame: PaneFrame } & Pick<StepTypeKindsProps, 'syncTag' | 'hasSyncedProducts' | 'onSaved'>) {
  const { t } = useTranslation('onboarding');
  const { isAdmin } = useRole();
  const admin = isAdmin();
  const buffer = useProductSyncSettingsBuffer();
  const scan = useCoverageScan(true, syncTag);
  const hold = useSavedHold();
  const hintId = useId();
  const saveRef = useRef<HTMLButtonElement>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const server = buffer.serverSettings;
  // Picks the admin changed, keyed by lower-cased type name. Everything else
  // reads its saved kind, so a save (or a change made elsewhere) re-syncs.
  const [edits, setEdits] = useState<Record<string, ProductTypeKind>>({});
  useEffect(() => {
    setEdits({});
  }, [server]);

  const types = scan.seenProductTypes;
  const pickFor = (name: string): ProductTypeKind => edits[typeKey(name)] ?? savedKind(server, name);

  // The lists the save would write: the saved lists with every changed pick
  // applied. Types the scan did not list keep their saved entries.
  const nextLists = useMemo(() => {
    let lists = { customTypes: [...server.customTypes], mixedTypes: [...(server.mixedTypes ?? [])] };
    for (const name of types) {
      const edit = edits[typeKey(name)];
      if (edit !== undefined && edit !== savedKind(server, name)) {
        lists = applyProductTypeKind(lists, name, edit);
      }
    }
    return lists;
  }, [server, types, edits]);

  const changed = types.some((name) => {
    const edit = edits[typeKey(name)];
    return edit !== undefined && edit !== savedKind(server, name);
  });
  const noneCustom = nextLists.customTypes.length === 0 && nextLists.mixedTypes.length === 0;
  const settingsReady = !buffer.isLoading && !buffer.isError;
  const showNeedOne = settingsReady && types.length > 0 && noneCustom;
  const saveDisabled = !admin || !settingsReady || buffer.isSaving || !changed || noneCustom;

  const save = () => {
    buffer.savePatch(
      { customTypes: nextLists.customTypes, mixedTypes: nextLists.mixedTypes },
      {
        onSuccess: () => {
          hold.markSaved();
          onSaved();
        },
        successMessage: t('stepTypeKinds.saved'),
      },
    );
  };

  const onSave = () => {
    const next = { ...server, customTypes: nextLists.customTypes, mixedTypes: nextLists.mixedTypes };
    if (hasSyncedProducts && customDecisionChanged(server, next)) {
      setConfirmOpen(true);
      return;
    }
    save();
  };

  const options = KIND_ORDER.map((kind) => ({ value: kind, label: t(KIND_LABEL_KEY[kind]) }));
  const firstPageLanded = scan.coverage !== null;
  const rescanButton = (
    <Button variant="outline" size="sm" className="h-7 gap-1.5 px-2.5 text-xs" onClick={scan.rescan}>
      <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
      {t('stepTypeKinds.rescan')}
    </Button>
  );

  // The footer hint says why Save is not offered (read with the button).
  const hint = !admin ? t('stepSyncTag.adminOnly') : showNeedOne ? t('stepTypeKinds.needOne') : undefined;

  return (
    <StepPane
      {...frame}
      footerHint={
        hint && (
          <span id={hintId} className={admin ? 'text-amber-700' : undefined}>
            {hint}
          </span>
        )
      }
      primary={
        <SavePrimaryButton
          ref={saveRef}
          phase={hold.phase(buffer.isSaving)}
          disabled={saveDisabled}
          onClick={onSave}
          describedBy={hint ? hintId : undefined}
        >
          <Save className="h-3.5 w-3.5" aria-hidden="true" />
          {t('stepTypeKinds.save')}
        </SavePrimaryButton>
      }
    >
      <div className="space-y-4">
        {scan.error ? (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2">
            <p className="text-xs text-destructive">{t('stepTypeKinds.scanError')}</p>
            {rescanButton}
          </div>
        ) : !firstPageLanded ? (
          <p className="flex items-center gap-2 text-xs text-muted-foreground" role="status">
            <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
            {t('stepTypeKinds.scanning')}
          </p>
        ) : scan.isComplete && types.length === 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-xs text-muted-foreground">{t('stepTypeKinds.empty')}</p>
            {rescanButton}
          </div>
        ) : null}

        {types.length > 0 && (
          <ul className="divide-y rounded-lg border">
            {types.map((name) => {
              const pick = pickFor(name);
              return (
                <li key={typeKey(name)} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-3 py-2">
                  <span className="min-w-0 text-sm font-medium [overflow-wrap:anywhere]">{name}</span>
                  {admin ? (
                    <SegmentedControl
                      options={options}
                      value={pick}
                      onChange={(kind) => setEdits((prev) => ({ ...prev, [typeKey(name)]: kind }))}
                      disabled={!settingsReady || buffer.isSaving}
                      ariaLabel={t('stepTypeKinds.rowAria', { name })}
                      tone="strong"
                    />
                  ) : (
                    <span className="text-xs text-muted-foreground">{t(KIND_LABEL_KEY[pick])}</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {firstPageLanded && !scan.isComplete && !scan.error && (
          <p className="text-xs text-muted-foreground" role="status">
            {t('stepTypeKinds.partial')}
          </p>
        )}

        {types.length > 0 && (
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">{t('stepTypeKinds.mixedHint')}</p>
            <p className="text-xs text-muted-foreground">{t('stepTypeKinds.noTypeHint')}</p>
          </div>
        )}

        {buffer.isError && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2">
            <p className="text-xs text-destructive">{t('stepSyncTag.loadError')}</p>
            <Button variant="outline" size="sm" className="h-7 px-2.5 text-xs" onClick={buffer.refetch}>
              {t('states.retry')}
            </Button>
          </div>
        )}

        <SkuChangeConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          onConfirm={save}
          returnFocusRef={saveRef}
        />
      </div>
    </StepPane>
  );
}
